import type { FC, ReactNode } from 'react';

import { useSearchableAccountOptions } from '@zen/Accounts/hooks/useSearchableAccountOptions';
import useGlobalPermissions from '@zen/Auth/useGlobalPermissions';
import { TextLink } from '@zen/DesignSystem';
import type { Option } from '@zen/DesignSystem/types';
import { AccountStatus, OrganizationalRoleEnum } from '@zen/graphql/types.generated';
import { accountsRoutes } from '@zen/Routes';
import { stringifyQueryParams } from '@zen/utils/QueryParams';
import type { Nullable, Optional } from '@zen/utils/typescript';

import FormSelect from '../FormSelect';

interface Props {
  assignableOnly?: boolean;
  error?: boolean;
  isLoading?: boolean;
  isRequired?: boolean;
  label?: string;
  name: string;
  noMargin?: boolean;
  onChange?: (value: Nullable<string>, options: Option<string>[]) => void;
  organizationalRole: OrganizationalRoleEnum[];
  selectedAccountIds?: string[];
  status?: AccountStatus[];
  value?: Optional<string>;
  variant?: 'inline' | 'default';
}

const FormAccountSelect: FC<Props> = (props) => {
  const { assignableOnly, onChange, organizationalRole, selectedAccountIds, status, value } = props;
  const { check } = useGlobalPermissions();
  const canCreateAccounts: boolean = check('platform.canCreateAccounts');

  const selectedValues = value ? [value] : selectedAccountIds;

  const { options, loading, onSearch } = useSearchableAccountOptions({
    assignableOnly,
    organizationalRole,
    selectedValues,
    status
  });

  const renderFooter = canCreateAccounts
    ? (): ReactNode => {
        return (
          <TextLink
            linkTo={{
              pathname: accountsRoutes.accountsCreate.getUrl(),
              search: stringifyQueryParams({
                organizationalRole: organizationalRole[0]
              })
            }}
          >
            Create new account
          </TextLink>
        );
      }
    : undefined;

  const handleChange = (newValue: Nullable<string>) => {
    onChange?.(newValue, options);
  };

  return (
    <FormSelect
      {...props}
      dropdownFooterRenderer={renderFooter}
      isLoading={loading}
      labelPlacement="left"
      onChange={handleChange}
      onInputChange={onSearch}
      options={options}
      placeholder="Search for an account..."
      renderMenuInPortal={true}
      shouldFilterResults={false}
    />
  );
};

export default FormAccountSelect;
export type { Props as FormAccountSelectProps };
